// components/MembershipInfo.tsx
import { useLanguage } from "@/context/LanguageContext";
import type { PromoCodeInfo } from "@/types";
import { ESTATE_LIMITS } from "@/utils/membershipLimits";
import React from "react";
import { Panel } from "@/components/ui/Panel";

interface MembershipInfoProps {
  membership: string | null;
  promoCodeInfo?: PromoCodeInfo | null;
}

const MembershipInfo: React.FC<MembershipInfoProps> = ({
  membership,
  promoCodeInfo,
}) => {
  const { language } = useLanguage();
  const copy = {
    cs: {
      basic: [
        "Ukázka hlídacího psa na 14 dní zdarma.",
        "Bez platební karty.",
        "1 hlídací pes, přístup ke všem funkcím.",
        `${ESTATE_LIMITS.BASIC} nemovitost v portfoliu.`,
      ],
      standard: [
        "2 hlídací psi, reportuje jednou denně.",
        `${ESTATE_LIMITS.STANDARD} nemovitosti v portfoliu.`,
        "Vhodné když nemáte čas hlídat weby. Na konci dne Vám udělá report o nových nabídkách.",
      ],
      premium: [
        "3 hlídací psi, reportuje několikrát denně.",
        `${ESTATE_LIMITS.PREMIUM} nemovitostí v portfoliu.`,
        "Vhodné když nemáte čas hlídat weby, ale chcete mít náskok. Každé 2h udělá report o nových nabídkách.",
      ],
      professional: [
        "5 hlídacích psů, reportuje okamžitě.",
        `${ESTATE_LIMITS.PROFESSIONAL} nemovitostí v portfoliu.`,
        "Úprava hledání na míru.",
        "Podpora 24/7.",
        "Přednostní přístup k novým funkcím.",
        "Vhodné pro investory, realitní makléře. Čas hraje největší roli.",
      ],
      free: "Členství zdarma",
      lifetime: "bez časového omezení",
      discount: "sleva",
      watchdogOne: "hlídací pes",
      watchdogMany: "hlídací psi",
      properties: "nemovitostí v portfoliu",
    },
    en: {
      basic: [
        "14-day free watchdog demo.",
        "No payment card required.",
        "1 watchdog with access to all features.",
        `${ESTATE_LIMITS.BASIC} property in the portfolio.`,
      ],
      standard: [
        "2 watchdogs with one daily report.",
        `${ESTATE_LIMITS.STANDARD} properties in the portfolio.`,
        "Good when you do not have time to monitor real estate sites. At the end of the day, it sends you a report with new listings.",
      ],
      premium: [
        "3 watchdogs with reports several times per day.",
        `${ESTATE_LIMITS.PREMIUM} properties in the portfolio.`,
        "Good when you want to stay ahead. Every 2 hours, it sends a report with new listings.",
      ],
      professional: [
        "5 watchdogs with instant reporting.",
        `${ESTATE_LIMITS.PROFESSIONAL} properties in the portfolio.`,
        "Custom search adjustments.",
        "24/7 support.",
        "Priority access to new features.",
        "Good for investors and real estate agents where timing matters most.",
      ],
      free: "Membership free of charge",
      lifetime: "with no time limit",
      discount: "discount",
      watchdogOne: "watchdog",
      watchdogMany: "watchdogs",
      properties: "properties in the portfolio",
    },
  }[language];

  if (!membership) return null;

  let message: string[] = [];

  if (promoCodeInfo?.valid) {
    const promoMembership = promoCodeInfo.membership ?? membership;
    const estateLimit =
      promoMembership && promoMembership in ESTATE_LIMITS
        ? ESTATE_LIMITS[promoMembership as keyof typeof ESTATE_LIMITS]
        : null;
    const promoDescription = promoCodeInfo.free
      ? `${copy.free}${promoCodeInfo.lifetime ? ` ${copy.lifetime}` : ""}.`
      : `${promoCodeInfo.discountPercent ?? 0}% ${copy.discount}${
          promoCodeInfo.lifetime ? ` ${copy.lifetime}` : ""
        }.`;

    message = [
      promoDescription,
      ...(promoCodeInfo.maxWatchDogs == null
        ? []
        : [
            `${promoCodeInfo.maxWatchDogs} ${
              promoCodeInfo.maxWatchDogs === 1
                ? copy.watchdogOne
                : copy.watchdogMany
            }.`,
          ]),
      ...(estateLimit == null ? [] : [`${estateLimit} ${copy.properties}.`]),
    ];
  } else {
    switch (membership) {
      case "BASIC":
        message = copy.basic;
        break;
      case "STANDARD":
        message = copy.standard;
        break;
      case "PREMIUM":
        message = copy.premium;
        break;
      case "PROFESSIONAL":
        message = copy.professional;
        break;
      default:
        message = [];
    }
  }

  return (
    <Panel variant="compact" className="p-4">
      <ul className="space-y-2 text-sm font-medium leading-5 text-softiq-copy-muted">
        {message.map((line, i) => (
          <li key={i} className="flex gap-2">
            <span className="text-softiq-primary" aria-hidden>
              •
            </span>
            <span>{line}</span>
          </li>
        ))}
      </ul>
    </Panel>
  );
};

export default MembershipInfo;
